import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import Markdown from 'preact-markdown';

import ShotTarget from '../tv/shotTarget';
import { bulletMm, formatShotValue } from '../../lib/pistolTargets';
import { getStageTypeDisplay } from '../../lib/conventionResults';
import { buildShooterSeries, formatSeconds } from '../../lib/shooterShots';
import { buildShotAdvice } from '../../lib/shotAdvice';
import {
    SIGHT_CLICK_PISTOLS, CUSTOM_PISTOL, resolveSight, clicksFor,
} from '../../lib/sightClicks';

// Series cards: two per row on a phone, wider on desktop so the target is
// readable on a big screen (sizes in custom.css, .shooter-shots-series). The
// shot values sit under the target as wrapping chips, large enough to read at
// arm's length: five on one row on desktop, three plus two on a phone.

/*
 * Drawer «shooterShots»: one shooter's Megalink shots on one Lag, every series
 * of the program on its own target with the shot list underneath — opened from
 * the Megalink icon on the result rows of the convention page. Tilleggsmodul
 * (C, shooting); registered in product/drawers-other.js.
 *
 * Data: GET /api/megalinkshots/live?convention=&stage= (the replay read: any
 * Lag, open or finished, access = the convention's own rules). The response
 * holds every shot of the Lag; lib/shooterShots.js keeps this shooter's lane.
 *
 * Advice: lib/shotAdvice.js (rule-based, every counted shot on the page, all
 * users) and POST /api/ai/shooter-advice (Claude as coach, admins only).
 */

function SeriesCard({ series, stageType }) {
    const muted = series.kind !== 'series';
    return (
        <div
            class={`shooter-shots-series d-flex flex-column rounded border p-2 ${muted ? 'text-body-secondary' : ''}`}
        >
            <div class='d-flex justify-content-between align-items-baseline px-1 mb-1'>
                <strong class='series-label'>{series.label}</strong>
                <span class='series-sum' style='font-variant-numeric: tabular-nums;'>
                    {series.shots.length > 0 ? `${series.total.sum} (*${series.total.inner})` : '–'}
                </span>
            </div>
            <ShotTarget target={series.target} shots={series.shots} bulletMm={bulletMm(stageType)} size='100%' />
            <div class='shot-list d-flex flex-wrap justify-content-center mt-2'>
                {series.shots.map((shot, i) => (
                    <span key={shot.shotNo || i} class='shot-cell d-inline-flex flex-column align-items-center'>
                        <span class='shot-value rounded bg-body-secondary px-1' title={`${i + 1}`}>
                            {formatShotValue(shot)}
                        </span>
                        {Number.isInteger(shot.split) && (
                            <small class='shot-split text-body-secondary' style='font-size: 0.7rem; font-variant-numeric: tabular-nums;'>
                                +{formatSeconds(shot.split)}
                            </small>
                        )}
                    </span>
                ))}
            </div>
            {Number.isInteger(series.duration) && (
                <div class='series-duration small text-body-secondary text-center mt-1' style='font-variant-numeric: tabular-nums;'>
                    <i class='fa-duotone fa-stopwatch me-1' />
                    <Text id='convention.shooterShots-duration' fields={{ seconds: formatSeconds(series.duration) }}>
                        {formatSeconds(series.duration)} s from first to last shot
                    </Text>
                </div>
            )}
        </div>
    );
}

// Millimetres with a Norwegian decimal comma, no sign.
function fmtMm(value) {
    return Math.abs(value).toFixed(1).replace('.', ',');
}

const SIDE = {
    left: <Text id='convention.shooterShots-advice-left'>left</Text>,
    right: <Text id='convention.shooterShots-advice-right'>right</Text>,
    high: <Text id='convention.shooterShots-advice-high'>high</Text>,
    low: <Text id='convention.shooterShots-advice-low'>low</Text>,
};
const MOVE = {
    left: <Text id='convention.shooterShots-advice-move-left'>to the left</Text>,
    right: <Text id='convention.shooterShots-advice-move-right'>to the right</Text>,
    up: <Text id='convention.shooterShots-advice-move-up'>up</Text>,
    down: <Text id='convention.shooterShots-advice-move-down'>down</Text>,
};
const SPREAD = {
    tight: <Text id='convention.shooterShots-advice-spread-tight'>tight</Text>,
    medium: <Text id='convention.shooterShots-advice-spread-medium'>medium</Text>,
    wide: <Text id='convention.shooterShots-advice-spread-wide'>wide</Text>,
};

function Both({ first, second }) {
    // "right and down" — one or both parts.
    if (first && second) {
        return <span>{first} <Text id='convention.shooterShots-advice-and'>and</Text> {second}</span>;
    }
    return <span>{first || second}</span>;
}

// «3 clicks to the left and 2 clicks down» — an axis without a click value
// falls back to its mm figure so the shooter still gets the whole move.
function ClickMove({ advice, clicks }) {
    const { mpi, move } = advice;
    const part = (axis, count, mm) => {
        if (!move[axis]) return null;
        return count > 0
            ? <span>{count} <Text id='convention.shooterShots-advice-clicks'>clicks</Text> {MOVE[move[axis]]}</span>
            : <span>{fmtMm(mm)} mm {MOVE[move[axis]]}</span>;
    };
    return <Both first={part('horizontal', clicks.horizontal, mpi.x)} second={part('vertical', clicks.vertical, mpi.y)} />;
}

// Pistol picker for the click values (lib/sightClicks.js). `sightChoice` is
// { pistol, windageMm, elevationMm }; the custom inputs show for «custom».
function SightPicker({ sightChoice = {}, onSightChange }) {
    const custom = sightChoice.pistol === CUSTOM_PISTOL;
    return (
        <span class='shooter-shots-sight-picker d-inline-flex flex-wrap align-items-center gap-1'>
            <select
                class='form-select form-select-sm w-auto'
                name='pistol'
                value={sightChoice.pistol || ''}
                onChange={onSightChange}
                aria-label='Pistol'
            >
                <option value=''><Text id='convention.shooterShots-advice-pistol'>Pistol…</Text></option>
                {SIGHT_CLICK_PISTOLS.map(p => (
                    <option key={p.key} value={p.key}>{p.name} ({p.windageMm}/{p.elevationMm} mm)</option>
                ))}
                <option value={CUSTOM_PISTOL}><Text id='convention.shooterShots-advice-pistol-custom'>Other pistol</Text></option>
            </select>
            {custom && (
                <span class='d-inline-flex align-items-center gap-1 small'>
                    <input
                        type='number'
                        class='form-control form-control-sm'
                        style='width: 4.5rem;'
                        name='windageMm'
                        min='1'
                        step='1'
                        placeholder='Side'
                        value={sightChoice.windageMm || ''}
                        onInput={onSightChange}
                        aria-label='mm per click, windage'
                    />
                    <input
                        type='number'
                        class='form-control form-control-sm'
                        style='width: 4.5rem;'
                        name='elevationMm'
                        min='1'
                        step='1'
                        placeholder='Høyde'
                        value={sightChoice.elevationMm || ''}
                        onInput={onSightChange}
                        aria-label='mm per click, elevation'
                    />
                    <Text id='convention.shooterShots-advice-mm-per-click'>mm per click</Text>
                </span>
            )}
        </span>
    );
}

function AdviceCard({
    advice, isAdmin = false, ai = {}, onAiAdvice, sightChoice = {}, onSightChange,
}) {
    if (!advice) return null;
    const {
        mpi, centered, direction, move, spread, gain, firstShots, shotCount,
    } = advice;
    const sightAmount = [
        direction.horizontal ? `${fmtMm(mpi.x)} mm` : '',
        direction.vertical ? `${fmtMm(mpi.y)} mm` : '',
    ].filter(Boolean).join(' / ');
    const sight = resolveSight(sightChoice.pistol, sightChoice);
    const clicks = clicksFor(advice, sight);
    const useClicks = !!sight && (clicks.horizontal > 0 || clicks.vertical > 0);
    return (
        <div class='shooter-shots-advice card mb-3'>
            <div class='card-body py-2'>
                <div class='d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1'>
                    <strong><i class='fa-duotone fa-bullseye-arrow me-1' /><Text id='convention.shooterShots-advice-title'>Advice</Text></strong>
                    {isAdmin && (
                        <button type='button' class='btn btn-sm btn-outline-primary shooter-shots-ai-button' onClick={onAiAdvice} disabled={ai.loading}>
                            {ai.loading ? <i class='fa-duotone fa-spinner fa-spin me-1' /> : <i class='fa-duotone fa-sparkles me-1' />}
                            <Text id='convention.shooterShots-ai-button'>AI analysis</Text>
                        </button>
                    )}
                </div>
                <ul class='mb-1 ps-3'>
                    <li class='shooter-shots-advice-mpi'>
                        <Text id='convention.shooterShots-advice-mpi'>Mean point of impact:</Text>{' '}
                        {fmtMm(mpi.x)} mm {SIDE[mpi.x < 0 ? 'left' : 'right']}, {fmtMm(mpi.y)} mm {SIDE[mpi.y < 0 ? 'low' : 'high']}.
                    </li>
                    <li class='shooter-shots-advice-sight'>
                        {centered ? (
                            <Text id='convention.shooterShots-advice-centered'>The group is centred – no sight adjustment needed.</Text>
                        ) : (
                            <span>
                                <strong><Text id='convention.shooterShots-advice-move'>Move the sight</Text>{' '}
                                    {useClicks
                                        ? <ClickMove advice={advice} clicks={clicks} />
                                        : <Both first={move.horizontal ? MOVE[move.horizontal] : null} second={move.vertical ? MOVE[move.vertical] : null} />}
                                </strong>
                                {' '}(<Text id='convention.shooterShots-advice-move-amount'>about</Text> {sightAmount} <Text id='convention.shooterShots-advice-on-target'>on the target</Text>).
                            </span>
                        )}
                    </li>
                    {!centered && (
                        <li class='shooter-shots-advice-pistol list-unstyled ms-n3 mt-1'>
                            <SightPicker sightChoice={sightChoice} onSightChange={onSightChange} />
                        </li>
                    )}
                    <li class='shooter-shots-advice-spread'>
                        <Text id='convention.shooterShots-advice-spread'>Group size:</Text> {SPREAD[spread.label]}{' '}
                        (<Text id='convention.shooterShots-advice-spread-mean'>average</Text> {fmtMm(spread.meanRadiusMm)} mm <Text id='convention.shooterShots-advice-spread-from'>from the mean point</Text>).
                    </li>
                    {gain.delta > 0 && (
                        <li class='shooter-shots-advice-gain'>
                            <Text id='convention.shooterShots-advice-gain' fields={{ delta: gain.delta, centered: gain.centered, actual: gain.actual }}>
                                A centred group would have scored about +{gain.delta} points ({gain.centered} instead of {gain.actual}).
                            </Text>
                        </li>
                    )}
                    {firstShots && (
                        <li class='shooter-shots-advice-first'>
                            <Text id='convention.shooterShots-advice-firstShots'>The first shot of each series lands</Text>{' '}
                            <Both first={firstShots.horizontal ? SIDE[firstShots.horizontal] : null} second={firstShots.vertical ? SIDE[firstShots.vertical] : null} />.
                        </li>
                    )}
                </ul>
                <small class='text-body-secondary'>
                    <Text id='convention.shooterShots-advice-basis' fields={{ count: shotCount }}>Based on {shotCount} counted shots; sighters are left out.</Text>
                </small>
                {ai.error && <div class='alert alert-warning mt-2 mb-0 py-1 shooter-shots-ai-error'>{ai.error}</div>}
                {ai.text && (
                    <div class='shooter-shots-ai mt-2 pt-2 border-top'>
                        <small class='text-body-secondary d-block mb-1'>
                            <i class='fa-duotone fa-sparkles me-1' />
                            <Text id='convention.shooterShots-ai-title'>AI coach</Text>
                            {ai.fromCache ? <span> · <Text id='convention.shooterShots-ai-cached'>from cache</Text></span> : null}
                        </small>
                        <Markdown markdown={ai.text} />
                    </div>
                )}
            </div>
        </div>
    );
}

// Pure view, unit tested on its own. `data` is buildShooterSeries() output.
export function ShooterShotsView({
    name, club, stageName, stageType, data, loading = false, error = '',
    advice = null, isAdmin = false, ai = {}, onAiAdvice, sightChoice = {}, onSightChange,
}) {
    const typeLabel = stageType ? (getStageTypeDisplay(stageType) || stageType) : '';
    return (
        <div class='shooter-shots p-3'>
            <div class='d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-3' style='padding-right: 60px;'>
                <div>
                    <h4 class='mb-0'>{name}</h4>
                    {club ? <div class='text-body-secondary'>{club}</div> : null}
                    <div class='small text-body-secondary'>
                        {stageName}
                        {stageName && typeLabel ? ' · ' : ''}
                        {typeLabel}
                        {data && data.lane ? <span> · <Text id='convention.shooterShots-lane'>Lane</Text> {data.lane}</span> : null}
                    </div>
                </div>
                {data && data.shotCount > 0 && (
                    <div class='text-end'>
                        <div class='shooter-shots-total fw-bold' style='font-size: 1.4rem; font-variant-numeric: tabular-nums;'>{data.total.sum} (*{data.total.inner})</div>
                        <small class='text-body-secondary'>
                            <Text id='convention.shooterShots-shots' fields={{ count: data.shotCount }}>{data.shotCount} shots</Text>
                        </small>
                    </div>
                )}
            </div>
            {loading && (
                <div class='text-body-secondary py-2'><i class='fa-duotone fa-spinner fa-spin me-1' /><Text id='convention.shooterShots-loading'>Loading shots…</Text></div>
            )}
            {!loading && error && (
                <div class='alert alert-warning'>{error}</div>
            )}
            {!loading && !error && (!data || data.shotCount === 0) && (
                <div class='alert alert-secondary shooter-shots-empty'>
                    <Text id='convention.shooterShots-empty'>No Megalink shots for this shooter on this Lag.</Text>
                </div>
            )}
            {!loading && !error && data && data.shotCount > 0 && (
                <AdviceCard
                    advice={advice}
                    isAdmin={isAdmin}
                    ai={ai}
                    onAiAdvice={onAiAdvice}
                    sightChoice={sightChoice}
                    onSightChange={onSightChange}
                />
            )}
            {!loading && !error && data && data.shotCount > 0 && (
                <div class='d-flex flex-wrap gap-2'>
                    {data.series.map(series => <SeriesCard key={series.key} series={series} stageType={data.stageType} />)}
                </div>
            )}
        </div>
    );
}

@observer
class ShooterShots extends Component {
    constructor(props) {
        super(props);
        this.state = {
            live: null, loading: true, error: '', ai: {}, sightChoice: {},
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async () => {
        const { conventionId, stageMd5 } = this.props;
        const query = { convention: conventionId, stage: stageMd5 };
        if (typeof window !== 'undefined' && window.location) {
            query.hostname = window.location.hostname;
        }
        const response = await util.fetchApi('/api/megalinkshots/live', { method: 'GET' }, query);
        if (response?.status === 200 && response.data) {
            this.setState({ live: response.data, loading: false, error: '' });
        } else {
            this.setState({ loading: false, error: response?.message || 'load-failed' });
        }
    }

    // Pistol picker: select (name=pistol) and the two custom mm inputs.
    onSightChange = e => {
        const { name, value } = e.target;
        this.setState(prev => ({ sightChoice: { ...prev.sightChoice, [name]: value } }));
    }

    // Admin only: Claude as coach on the same shots (reloaded server-side).
    loadAiAdvice = async () => {
        const { conventionId, stageMd5, participantId } = this.props;
        const { sightChoice } = this.state;
        this.setState({ ai: { loading: true } });
        const response = await util.fetchApi('/api/ai/shooter-advice', { method: 'POST' }, {
            convention: conventionId,
            stage: stageMd5,
            participant: participantId,
            labels: this.labelsFor(this.currentStage()),
            sight: resolveSight(sightChoice.pistol, sightChoice),
        });
        if (response?.status === 200 && response.data) {
            this.setState({ ai: { text: response.data.output_text || '', fromCache: !!response.data.fromCache } });
        } else {
            this.setState({ ai: { error: response?.message || 'load-failed' } });
        }
    }

    labelsFor(stage) {
        const { conventionResultStore } = this.props.stores;
        if (!stage || !conventionResultStore || typeof conventionResultStore.getParticipantFields !== 'function') {
            return [];
        }
        return conventionResultStore.getParticipantFields(stage);
    }

    currentStage() {
        const { stageMd5 } = this.props;
        const { conventionStore } = this.props.stores;
        const { live } = this.state;
        const { convention = {} } = conventionStore || {};
        return (convention.stages || []).find(s => s && s.md5 === stageMd5)
            || (live && live.stage ? { type: live.stage.type } : null);
    }

    render() {
        const {
            participantId, name, club,
        } = this.props;
        const { userStore } = this.props.stores;
        const {
            live, loading, error, ai, sightChoice,
        } = this.state;
        const stage = this.currentStage();
        const data = live ? buildShooterSeries({ live, participantId, labels: this.labelsFor(stage) }) : null;
        const stageName = (stage && stage.name) || (live && live.stage && live.stage.name) || '';
        const stageType = (stage && stage.type) || (live && live.stage && live.stage.type) || '';
        return (
            <ShooterShotsView
                name={name}
                club={club}
                stageName={stageName}
                stageType={stageType}
                data={data}
                advice={buildShotAdvice(data)}
                isAdmin={!!(userStore && userStore.isAdmin)}
                ai={ai}
                onAiAdvice={this.loadAiAdvice}
                sightChoice={sightChoice}
                onSightChange={this.onSightChange}
                loading={loading}
                error={error}
            />
        );
    }
}

export default ShooterShots;
